import React, { useState } from 'react';
import { CheckCircle2, Target, Activity, Heart, TrendingUp, Award, Users, Video, Home, Clock3 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHeader from '../components/PageHeader';
import AvaliacaoDialog from '../components/AvaliacaoDialog';
import { Button } from '../components/ui/button';

const beneficios = [
  { icon: Target, title: 'Avaliação inicial', text: 'Análise completa do seu ponto de partida, objetivos e historial.' },
  { icon: Activity, title: 'Plano de treino personalizado', text: 'Programa criado à sua medida, adaptado a si e ao seu ritmo.' },
  { icon: Users, title: 'Acompanhamento técnico', text: 'Treinador presente em cada sessão, focado nos seus resultados.' },
  { icon: Heart, title: 'Correção da postura e execução', text: 'Treine com segurança, evitando lesões e otimizando cada movimento.' },
  { icon: TrendingUp, title: 'Progressão ajustada', text: 'Cargas, volumes e exercícios evoluem consigo, sem estagnação.' },
  { icon: Award, title: 'Resultados consistentes', text: 'Transformação real, sustentável e duradoura no tempo.' }
];

const PersonalTraining = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  return (
    <div className="min-h-screen bg-black">
      <Header onOpenForm={() => setIsFormOpen(true)} />

      {/* Hero */}
      <PageHeader
        title="PERSONAL TRAINING"
        subtitle="Treino personalizado, acompanhamento próximo e resultados reais."
        backgroundImage="/assets/pt-hero.webp"
        backgroundPosition="center 35%"
        overlayOpacity={0.55}
      />

      {/* Secção: O que é treino personalizado? */}
      <section className="py-16 sm:py-20 md:py-24 bg-black border-b border-zinc-900" data-testid="pt-what-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <h2
            className="text-white uppercase mb-6"
            style={{
              fontFamily: "'BebasNeue-Book', sans-serif",
              fontSize: "clamp(36px, 5.5vw, 64px)",
              letterSpacing: "0.04em",
              lineHeight: "1.05"
            }}
          >
            O que é treino personalizado?
          </h2>
          <div className="w-20 h-1 bg-yellow-400 mx-auto mb-8" />
          <div
            className="text-gray-300 space-y-4 mb-10 max-w-3xl mx-auto"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 300,
              fontSize: "clamp(15px, 1.05vw, 17px)",
              lineHeight: "1.6",
              letterSpacing: "0.01em",
              textAlign: "justify"
            }}
          >
            <p>O treino personalizado é um método de exercício físico planeado e adaptado às necessidades, objetivos (emagrecimento, força, mobilidade), histórico de saúde e limitações específicas de cada indivíduo.</p>
            <p>Orientado por um personal trainer, oferece acompanhamento próximo, maior segurança contra lesões e máxima eficiência nos resultados.</p>
            <p>O Personal Trainer avalia o seu ponto de partida, considera os seus gostos e objetivos e cria um plano totalmente adaptado a si.</p>
            <p>À medida que evolui, o plano é ajustado (alterando cargas, intensidade e exercícios) garantindo progressão contínua.</p>
          </div>
          <Button
            onClick={() => setIsFormOpen(true)}
            data-testid="pt-what-cta-btn"
            className="btn-mobile-cta bg-yellow-400 hover:bg-yellow-500 text-black uppercase px-10 py-6 border-2 border-yellow-400 hover:shadow-2xl transform hover:-translate-y-1 transition-all"
            style={{
              fontFamily: "'BebasNeue-Book', sans-serif",
              fontWeight: "normal",
              fontSize: "33.6px",
              letterSpacing: "-0.02em"
            }}
          >
            <span style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>MARCAR AVALIAÇÃO FÍSICA GRATUITA</span>
          </Button>
        </div>
      </section>

      {/* Imagem + Texto - Acompanhamento em cada etapa */}
      <section className="py-16 sm:py-20 md:py-28 bg-black" data-testid="pt-acompanhamento-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-10 md:gap-14 lg:gap-20 items-center">
            {/* Imagem */}
            <div className="relative order-2 md:order-1">
              <img
                src="/assets/pt-acompanhamento.webp"
                alt="Acompanhamento Personal Training"
                loading="lazy"
                className="w-full h-auto aspect-[4/5] object-cover shadow-2xl"
              />
              <div className="hidden md:block absolute -bottom-6 -right-6 w-32 h-32 bg-yellow-400 -z-10" />
            </div>

            {/* Texto */}
            <div className="order-1 md:order-2">
              <p
                className="text-yellow-400 uppercase mb-4"
                style={{
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  fontSize: "16px",
                  letterSpacing: "0.25em"
                }}
              >
                A nossa abordagem
              </p>
              <h3
                className="text-white uppercase mb-6"
                style={{
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  fontSize: "clamp(32px, 4.5vw, 60px)",
                  letterSpacing: "0.04em",
                  lineHeight: "1.05"
                }}
              >
                Acompanhamento em cada etapa
              </h3>
              <p
                className="text-gray-300 mb-8"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 300,
                  fontSize: "clamp(15px, 1.05vw, 18px)",
                  lineHeight: "1.75"
                }}
              >
                Mais do que prescrever exercícios, acompanhamos todo o processo. Ajustamos o treino, monitorizamos a evolução e ajudamos a manter a motivação para que cada sessão contribua para a sua melhor versão.
              </p>

              {/* Lista de pilares */}
              <ul className="space-y-3">
                {[
                  'Sessões 1 para 1 com o seu trainer',
                  'Avaliações periódicas de progresso',
                  'Ajuste contínuo do plano de treino',
                  'Apoio motivacional e técnico'
                ].map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-gray-200"
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: 300,
                      fontSize: "0.98rem",
                      lineHeight: "1.5"
                    }}
                  >
                    <CheckCircle2 className="text-yellow-400 flex-shrink-0 mt-0.5" size={20} strokeWidth={2.2} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Grid de Benefícios */}
      <section
        className="relative py-16 sm:py-20 md:py-24 border-y border-zinc-900 ult-transformacao-bg bg-cover bg-center md:bg-fixed"
        style={{ backgroundImage: "url('/assets/ult-transformacao.webp')" }}
        data-testid="pt-benefits-section"
      >
        <div className="absolute inset-0 bg-black/75" />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center mb-14">
            <p
              className="text-yellow-400 uppercase mb-3"
              style={{
                fontFamily: "'BebasNeue-Book', sans-serif",
                fontSize: "16px",
                letterSpacing: "0.25em"
              }}
            >
              O que está incluído
            </p>
            <h3
              className="text-white uppercase"
              style={{
                fontFamily: "'BebasNeue-Book', sans-serif",
                fontSize: "clamp(32px, 4.5vw, 56px)",
                letterSpacing: "0.04em",
                lineHeight: "1"
              }}
            >
              Tudo aquilo de que precisa
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {beneficios.map((b, idx) => {
              const Icon = b.icon;
              return (
                <div
                  key={idx}
                  data-testid={`pt-benefit-${idx}`}
                  className="group bg-black/70 backdrop-blur-sm border border-zinc-800 hover:border-yellow-400/60 p-7 md:p-8 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-yellow-400 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="text-black" size={22} strokeWidth={2.2} />
                  </div>
                  <h4
                    className="text-white uppercase mb-3"
                    style={{
                      fontFamily: "'BebasNeue-Book', sans-serif",
                      fontSize: "clamp(22px, 2vw, 28px)",
                      letterSpacing: "0.04em",
                      lineHeight: "1.1"
                    }}
                  >
                    {b.title}
                  </h4>
                  <p
                    className="text-gray-400"
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: 300,
                      fontSize: "0.95rem",
                      lineHeight: "1.6"
                    }}
                  >
                    {b.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Introdução - Treino feito à sua medida */}
      <section className="py-16 sm:py-20 md:py-28 bg-white" data-testid="pt-intro-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
          <h2
            className="text-black uppercase mb-8"
            style={{
              fontFamily: "'BebasNeue-Book', sans-serif",
              fontSize: "clamp(36px, 5.5vw, 72px)",
              letterSpacing: "0.04em",
              lineHeight: "1.05"
            }}
          >
            Treino feito à sua medida
          </h2>
          <div className="w-20 h-1 bg-yellow-400 mx-auto mb-8" />
          <p
            className="text-gray-700"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 300,
              fontSize: "clamp(15px, 1.15vw, 19px)",
              lineHeight: "1.75",
              letterSpacing: "0.01em"
            }}
          >
            Na Ultimate Fit, cada plano é criado de acordo com o seu ponto de partida, objetivos e condição física atual. O nosso acompanhamento garante que treina com segurança, confiança e máxima eficiência.
          </p>
        </div>
      </section>

      {/* Secção: Treino Online Personalizado */}
      <section className="py-16 sm:py-20 md:py-28 bg-zinc-950 border-y border-zinc-900" data-testid="pt-online-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-10 md:gap-14 lg:gap-20 items-center">
            {/* Texto */}
            <div className="order-2 md:order-1">
              <p
                className="text-yellow-400 uppercase mb-4"
                style={{
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  fontSize: "16px",
                  letterSpacing: "0.25em"
                }}
              >
                Treine onde quiser
              </p>
              <h3
                className="text-white uppercase mb-6"
                style={{
                  fontFamily: "'BebasNeue-Book', sans-serif",
                  fontSize: "clamp(32px, 4.7vw, 60px)",
                  letterSpacing: "0.04em",
                  lineHeight: "1.05"
                }}
              >
                Treino Online Personalizado
              </h3>
              <div
                className="text-gray-300 space-y-4 mb-8"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 300,
                  fontSize: "clamp(15px, 1.05vw, 18px)",
                  lineHeight: "1.75",
                  letterSpacing: "0.01em"
                }}
              >
                <p>
                  O <span className="text-white">Treino Online</span> é um serviço de acompanhamento à distância com Personal Trainer, conduzido em direto por <span className="text-white">videochamada</span>. Treina com a presença e orientação do teu treinador, esteja onde estiveres.
                </p>
                <p>
                  Tudo começa com uma <span className="text-white">avaliação física</span>, presencial ou online, onde analisamos o teu ponto de partida, objetivos e condição física atual.
                </p>
                <p>
                  A partir daí é construído um <span className="text-white">plano 100% personalizado</span> aos teus objetivos, à tua condição física e adaptado ao material que tens disponível em casa.
                </p>
                <p>
                  Permite-te <span className="text-white">treinar em casa</span> ou em qualquer local — poupando deslocações e tempo, sem comprometer a qualidade do treino nem os resultados.
                </p>
              </div>

              {/* Pilares com ícones */}
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
                {[
                  { icon: Video, text: 'Sessões em direto por videochamada' },
                  { icon: Activity, text: 'Avaliação física presencial ou online' },
                  { icon: Home, text: 'Treina em casa ou onde estiveres' },
                  { icon: Clock3, text: 'Poupa tempo e evita deslocações' }
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-gray-200"
                      style={{
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 300,
                        fontSize: "0.95rem",
                        lineHeight: "1.5"
                      }}
                    >
                      <span className="w-9 h-9 flex-shrink-0 bg-yellow-400 flex items-center justify-center">
                        <Icon className="text-black" size={18} strokeWidth={2.2} />
                      </span>
                      <span className="pt-1.5">{item.text}</span>
                    </li>
                  );
                })}
              </ul>

            </div>

            {/* Imagem */}
            <div className="relative order-1 md:order-2">
              <img
                src="/assets/pt-online.webp"
                alt="Treino Online Personalizado por videochamada"
                loading="lazy"
                className="w-full h-auto aspect-[4/5] object-cover shadow-2xl"
              />
              <div className="hidden md:block absolute -bottom-6 -left-6 w-32 h-32 bg-yellow-400 -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section
        className="py-20 md:py-28 relative overflow-hidden"
        data-testid="pt-cta-section"
        style={{
          backgroundImage: "linear-gradient(rgba(0,0,0,0.72), rgba(0,0,0,0.72)), url('/assets/pt-comece.webp')",
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%'
        }}
      >
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2
            className="text-white uppercase mb-6"
            style={{
              fontFamily: "'BebasNeue-Book', sans-serif",
              fontSize: "clamp(36px, 5.5vw, 72px)",
              letterSpacing: "0.04em",
              lineHeight: "1.05"
            }}
          >
            Comece hoje o seu processo de transformação.
          </h2>
          <div className="w-20 h-1 bg-yellow-400 mx-auto mb-8" />
          <Button
            onClick={() => setIsFormOpen(true)}
            data-testid="pt-cta-btn"
            className="btn-mobile-cta bg-yellow-400 hover:bg-yellow-500 text-black uppercase px-10 py-6 border-2 border-yellow-400 hover:shadow-2xl transform hover:-translate-y-1 transition-all"
            style={{
              fontFamily: "'BebasNeue-Book', sans-serif",
              fontWeight: "normal",
              fontSize: "33.6px",
              letterSpacing: "-0.02em"
            }}
          >
            <span style={{ fontFamily: "'BebasNeue-Book', sans-serif", fontWeight: "normal" }}>MARCAR AVALIAÇÃO FÍSICA GRATUITA</span>
          </Button>
        </div>
      </section>

      <Footer />
      <AvaliacaoDialog isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </div>
  );
};

export default PersonalTraining;
